/**
 * `/api/workspace-variables` (YAG-831): variables set once per workspace and
 * read by every build and environment run in it. Names and when each was set
 * come back; a value is written and never read again.
 */
import { useCallback, useEffect, useState } from "react";
import type { WorkspaceVariable } from "@yagni/shared";

const BASE = "/api/workspace-variables";

export const WORKSPACE_VARIABLES_FAILED =
  "Workspace variables could not be reached. Try again.";

async function failure(response: Response): Promise<Error> {
  try {
    const body = (await response.json()) as { error?: unknown };
    if (typeof body.error === "string" && body.error) return new Error(body.error);
  } catch {
    // A body that is not JSON says nothing a person can act on.
  }
  return new Error(WORKSPACE_VARIABLES_FAILED);
}

/** Every name in the list that is well formed; anything else is dropped. */
function readVariables(body: unknown): WorkspaceVariable[] {
  const list = (body as { variables?: unknown } | null)?.variables;
  if (!Array.isArray(list)) return [];
  return list.filter(
    (entry): entry is WorkspaceVariable =>
      typeof entry === "object" &&
      entry !== null &&
      typeof (entry as WorkspaceVariable).name === "string" &&
      typeof (entry as WorkspaceVariable).setAt === "string",
  );
}

export async function listWorkspaceVariables(): Promise<WorkspaceVariable[]> {
  const response = await fetch(BASE, { credentials: "include" });
  if (!response.ok) throw await failure(response);
  return readVariables(await response.json());
}

export async function setWorkspaceVariables(
  values: Record<string, string>,
): Promise<void> {
  const set = Object.entries(values).map(([name, value]) => ({ name, value }));
  if (set.length === 0) return;
  const response = await fetch(BASE, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ set }),
  });
  if (!response.ok) throw await failure(response);
}

export async function removeWorkspaceVariable(name: string): Promise<void> {
  const response = await fetch(`${BASE}/${encodeURIComponent(name)}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!response.ok) throw await failure(response);
}

/**
 * The workspace's variables as the repository page reads them: the list, a
 * reload, and the last failure. A failed read leaves an empty list, so a
 * repository's Variables row reads every name as not set by the workspace
 * rather than breaking the page.
 */
export function useWorkspaceVariables(): {
  variables: WorkspaceVariable[];
  loaded: boolean;
  error: string | null;
  reload: () => Promise<void>;
} {
  const [variables, setVariables] = useState<WorkspaceVariable[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setVariables(await listWorkspaceVariables());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : WORKSPACE_VARIABLES_FAILED);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { variables, loaded, error, reload };
}
